import * as Clipboard from 'expo-clipboard';
import debounce from 'lodash.debounce';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  ListRenderItemInfo,
  TouchableOpacity,
  Image,
  TextInput,
  ViewStyle,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import Highlighter from '@components/HighlightWords/index';
import { AddMemberType } from '@components/Invite/SelectMembersList';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import {
  AssigneeRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { AddMembersInput } from '@components/TaskList/AnimatedSearchBox.web';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { ProjectRoleSelectorWeb } from '@components/TaskList/ProjectRoleSelector.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import {
  User,
  ProjectMemberRole,
  Group,
  useListGroupsQuery,
  ProjectMemberRoleAttributes,
  useCreateInviteMutation,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import useMe from '@hooks/useMe';
import { useMemberSearch } from '@hooks/useMemberSearch';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import theme from '@themes/theme';
import { getSuggestionEmail } from '@utils/emailSuggestion';
import { addOnboardingInvitees } from '@utils/onboardingInvitedMembers';
import { dedupeProjectMemberInvites } from '@utils/projects';

const getHoverRowStyle = (hover: boolean, isDisabled: boolean): ViewStyle => ({
  backgroundColor: hover ? theme.colors.grey01 : theme.colors.white,
  opacity: isDisabled ? 0.6 : 1,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
});

const emailExistsInEntities = (
  entities: Array<{ users?: Array<{ email?: string | null }> | null }>,
  email: string
) =>
  entities.some((e) =>
    (e.users ?? []).some((u) => (u.email || '').toLowerCase() === email)
  );

export type StringSearchType = {
  id?: string;
  title: string;
  role: ProjectMemberRole;
  __typename: string;
};

export type UserSearchType =
  | (Pick<
      User,
      | 'id'
      | 'name'
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'avatar'
      | '__typename'
    > & { role: ProjectMemberRole })
  | StringSearchType;

interface ProjectInviteOrAddMemberProps {
  projectId?: string;
  selectedUser?: AddMemberType[];
  existingProjectMembers?: AddMemberType[];
  onAddEmailInvitations?: (
    invitations: ProjectMemberRoleAttributes[],
    selectedUsers?: UserSearchType[],
    hasExternalUsers?: boolean
  ) => Promise<void> | void;
  onClose?: () => void;
  /** When false, the Admin role option is hidden in the role selector. */
  canAssignAdminRole: boolean;
  /** When true, pre-populated selectedUser entries count as changes, enabling the Send Invite button immediately. */
  treatInitialSelectionsAsChanges?: boolean;
}

type SelectedGroupType = Omit<Group, 'users'> & { users?: UserSearchType[] };

// renderableSelectedGroupList tags each user with this optional marker for
// display -- UserSearchType itself doesn't carry it.
type RenderableGroupMember = UserSearchType & {
  isDuplicateInSelection?: boolean;
};

const updateGroupUserRoleMapper = (
  g: SelectedGroupType,
  groupId: string,
  userId: string,
  role: ProjectMemberRole
): SelectedGroupType => {
  if (g.id !== groupId) return g;
  const nextUsers = (g.users ?? []).map((u) =>
    u.id === userId ? { ...u, role } : u
  );
  return { ...g, users: nextUsers };
};

const EmptyListBox = () => <Box />;

type RoleSelectorPopoverContentProps = {
  onClose: () => void;
  currentRole: ProjectMemberRole;
  onRoleChange: (role: ProjectMemberRole) => void;
  adminChangeConfig: {
    type: 'user' | 'email' | 'groupUser';
    id: string;
    groupId?: string;
    name: string;
  };
  onPendingAdminChange: (change: {
    type: 'user' | 'email' | 'groupUser';
    id: string;
    groupId?: string;
    name: string;
    role: ProjectMemberRole;
  }) => void;
  canAssignAdminRole: boolean;
};

const RoleSelectorPopoverContent = ({
  onClose,
  currentRole,
  onRoleChange,
  adminChangeConfig,
  onPendingAdminChange,
  canAssignAdminRole,
}: RoleSelectorPopoverContentProps) => (
  <ProjectRoleSelectorWeb
    onRoleSelect={(role) => {
      if (
        role === ProjectMemberRole.Admin &&
        currentRole !== ProjectMemberRole.Admin
      ) {
        onPendingAdminChange({ ...adminChangeConfig, role });
      } else {
        onRoleChange(role);
      }
      onClose();
    }}
    selectedRole={currentRole}
    isOwner={canAssignAdminRole}
  />
);

const getRoleLabel = (role: ProjectMemberRole): string => {
  if (role === ProjectMemberRole.Owner) return 'Owner';
  if (role === ProjectMemberRole.Admin) return 'Admin';
  if (role === ProjectMemberRole.Editor) return 'Editor';
  return 'Viewer';
};

type RoleSelectorWithTrashProps = {
  currentRole: ProjectMemberRole;
  onRoleChange: (role: ProjectMemberRole) => void;
  onRemove: () => void;
  removeLabel: string;
  adminChangeConfig: {
    type: 'user' | 'email' | 'groupUser';
    id: string;
    groupId?: string;
    name: string;
  };
  onPendingAdminChange: (change: {
    type: 'user' | 'email' | 'groupUser';
    id: string;
    groupId?: string;
    name: string;
    role: ProjectMemberRole;
  }) => void;
  canAssignAdminRole: boolean;
};

const RoleSelectorContext = createContext<Omit<
  RoleSelectorWithTrashProps,
  'onRemove' | 'removeLabel'
> | null>(null);

const RoleSelectorModal = ({ onClose }: { onClose: () => void }) => {
  const ctx = useContext(RoleSelectorContext);
  if (!ctx) return null;
  return (
    <RoleSelectorPopoverContent
      onClose={onClose}
      currentRole={ctx.currentRole}
      onRoleChange={ctx.onRoleChange}
      adminChangeConfig={ctx.adminChangeConfig}
      onPendingAdminChange={ctx.onPendingAdminChange}
      canAssignAdminRole={ctx.canAssignAdminRole}
    />
  );
};

const RoleSelectorWithTrash = ({
  currentRole,
  onRoleChange,
  onRemove,
  removeLabel,
  adminChangeConfig,
  onPendingAdminChange,
  canAssignAdminRole,
}: RoleSelectorWithTrashProps) => {
  const contextValue = useMemo(
    () => ({
      currentRole,
      onRoleChange,
      adminChangeConfig,
      onPendingAdminChange,
      canAssignAdminRole,
    }),
    [
      currentRole,
      onRoleChange,
      adminChangeConfig,
      onPendingAdminChange,
      canAssignAdminRole,
    ]
  );
  return (
    <RoleSelectorContext.Provider value={contextValue}>
      <>
        <BaseButtonV3
          noBorder={true}
          noRadius={false}
          noHover={false}
          buttonRadius='xs'
          modalRadius={8}
          showHoverWhenDropdown={true}
          mr='s'
          popModalComponent={RoleSelectorModal}>
          <Box
            width={86}
            mx='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='textPrimary' marginRight='xs'>
              {getRoleLabel(currentRole)}
            </Text>
            <Icon color='grey05' variant='s' name='ChevronDown' />
          </Box>
        </BaseButtonV3>
        <SimpleHoverable accessibilityLabel={removeLabel} onPress={onRemove}>
          {(hoverTrash) => (
            <Box
              width={32}
              height={32}
              borderRadius='xs'
              backgroundColor={hoverTrash ? 'grey01' : 'white'}
              alignItems='center'
              justifyContent='center'>
              <Icon
                color={hoverTrash ? 'alertRed' : 'grey05'}
                variant='s'
                name='Trash2'
              />
            </Box>
          )}
        </SimpleHoverable>
      </>
    </RoleSelectorContext.Provider>
  );
};

const DEBOUNCE_MS = 500;

export const ProjectInviteOrAddMember = ({
  projectId,
  selectedUser = [],
  existingProjectMembers = [],
  onClose,
  onAddEmailInvitations,
  canAssignAdminRole,
  treatInitialSelectionsAsChanges = false,
}: ProjectInviteOrAddMemberProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<TextInput>(null);
  // TA-10891 #19: the "Link copied" toast is portaled onto document.body so
  // it renders above the modal's overlay instead of under it (the app-level
  // ToastProvider sits beneath the RN Web <Modal> layer).
  const [showLinkCopiedToast, setShowLinkCopiedToast] = useState(false);
  const linkCopiedToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (linkCopiedToastTimerRef.current)
        clearTimeout(linkCopiedToastTimerRef.current);
    };
  }, []);
  const { t } = useTranslation();
  const { t: tModels } = useTranslation('models');
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();
  const { contacts, loading: contactsLoading } = useContacts();

  const defaultRole = ProjectMemberRole.Viewer;

  // Admin confirmation modal state
  const [showAdminConfirmModal, setShowAdminConfirmModal] = useState(false);
  const [pendingAdminChange, setPendingAdminChange] = useState<{
    type: 'user' | 'email' | 'groupUser' | 'allGroup';
    id: string;
    groupId?: string;
    role: ProjectMemberRole;
    name: string;
  } | null>(null);

  const {
    searchVal,
    setText,
    focusOnSearch,
    setFocusOnSearch,
    shouldShowDropdown,
    listContacts,
    listGroup: _listGroup,
    listTasktagUsers,
    expandedGroupIds,
    searchExpandedGroupIds,
    toggleGroupExpanded,
    toggleSearchGroupExpanded,
    hasSearched,
  } = useMemberSearch({
    contacts,
    meId: me?.id,
  });

  // TA-10891 #18: the member TextInput only renders once `focusOnSearch` is
  // true (it shows a placeholder otherwise), so switch to input mode on open.
  // `autoFocus` alone fires before the RN Web <Modal> is visible, so focus it
  // explicitly once it has rendered.
  useEffect(() => {
    setFocusOnSearch(true);
  }, [setFocusOnSearch]);
  useEffect(() => {
    if (!focusOnSearch) return;
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(focusTimer);
  }, [focusOnSearch]);

  // Copy Link tooltip state
  type CopyLinkTooltipMode = 'hover' | 'copied' | null;
  const [copyLinkTooltipMode, setCopyLinkTooltipMode] =
    useState<CopyLinkTooltipMode>(null);
  const [suppressCopyHoverTooltip, setSuppressCopyHoverTooltip] =
    useState(false);
  // Ghost text-button hover tint (TA-9005): tracked separately from the
  // tooltip state machine so the grey01 tint shows even while suppressed.
  const [isCopyLinkHovered, setIsCopyLinkHovered] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const COPY_LINK_TOAST_KEY = 'copy_link_toast_shown';

  // Copy Link functionality - same as project tab bar link button
  const copyToClipboard = useCallback(async (link: string) => {
    if (!link) return;
    await Clipboard.setStringAsync(link);
    setSuppressCopyHoverTooltip(true);
    setCopyLinkTooltipMode('copied');
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => {
      setCopyLinkTooltipMode(null);
      setSuppressCopyHoverTooltip(false);
    }, 2000);

    const alreadyShown = localStorage.getItem(COPY_LINK_TOAST_KEY);
    if (!alreadyShown) {
      localStorage.setItem(COPY_LINK_TOAST_KEY, '1');
      setShowLinkCopiedToast(true);
      if (linkCopiedToastTimerRef.current)
        clearTimeout(linkCopiedToastTimerRef.current);
      linkCopiedToastTimerRef.current = setTimeout(
        () => setShowLinkCopiedToast(false),
        3000
      );
    }
  }, []);

  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation();

  useEffect(() => {
    if (!createInviteData) return;
    const { createInvite } = createInviteData;
    if (createInvite) {
      const formattedLink = createInvite.replace(
        'register/register-with-email',
        'invited-user'
      );
      copyToClipboard(formattedLink);
    }
  }, [createInviteData]);

  const handleCopyLink = useCallback(() => {
    if (!projectId) return;
    createInviteMutation({ variables: { projectId } });
  }, [projectId, createInviteMutation]);

  const initialSelectedUsers = useMemo(
    () =>
      selectedUser.filter((t) => t.__typename === 'User') as UserSearchType[],
    [selectedUser]
  );
  const initialSelectedEmails = useMemo(
    () =>
      selectedUser
        .filter((t) => t.__typename !== 'User')
        .map((t) => {
          // Convert InvitedUser items to StringSearchType format
          // InvitedUser has name/email, but StringSearchType expects title
          if (t.__typename === 'InvitedUser') {
            const invitedUser = t as {
              email?: string;
              name?: string;
              role?: ProjectMemberRole;
            };
            return {
              id: (t as { id?: string }).id,
              title: invitedUser.email || invitedUser.name || '',
              role: invitedUser.role || defaultRole,
              __typename: 'StringSearchType',
            } as StringSearchType;
          }
          return t as StringSearchType;
        }),
    [selectedUser, defaultRole]
  );

  const [selectedUserList, setSelectedUserList] =
    useState<UserSearchType[]>(initialSelectedUsers);

  const [selectedEmailList, setSelectedEmailList] = useState<
    StringSearchType[]
  >(initialSelectedEmails);

  const [selectedGroupList, setSelectedGroupList] = useState<
    SelectedGroupType[]
  >([]);

  // Sync state when selectedUser prop changes (e.g., when modal is reopened)
  useEffect(() => {
    setSelectedUserList(initialSelectedUsers);
    setSelectedEmailList(initialSelectedEmails);
    setSelectedGroupList([]);
  }, [JSON.stringify(selectedUser)]);

  const selectedUserListRef = useRef<UserSearchType[]>(selectedUserList);
  const selectedEmailListRef = useRef<StringSearchType[]>(selectedEmailList);
  const selectedGroupListRef = useRef<SelectedGroupType[]>(selectedGroupList);

  useEffect(() => {
    selectedUserListRef.current = selectedUserList;
  }, [selectedUserList]);

  useEffect(() => {
    selectedEmailListRef.current = selectedEmailList;
  }, [selectedEmailList]);

  useEffect(() => {
    selectedGroupListRef.current = selectedGroupList;
  }, [selectedGroupList]);

  const { data: allGroupsData } = useListGroupsQuery();

  const allGroupsMap = useMemo(() => {
    const map = new Map<string, Group>();
    (allGroupsData?.listGroups ?? []).forEach((g: Group) => map.set(g.id, g));
    return map;
  }, [allGroupsData]);

  const listGroup = useMemo(() => {
    return _listGroup.map((searchGroup) => {
      const fullGroup = allGroupsMap.get(searchGroup.id);
      if (fullGroup && (!searchGroup.users || searchGroup.users.length === 0)) {
        return { ...searchGroup, users: fullGroup.users };
      }
      return searchGroup;
    });
  }, [_listGroup, allGroupsMap]);

  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserList.map((u) => u.id)),
    [selectedUserList]
  );

  // Combined set of all already-added user IDs (includes selected + group members)
  const allSelectedUserIdSet = useMemo(() => {
    const ids = new Set(selectedUserList.map((u) => u.id));
    selectedGroupList.forEach((g) => {
      g.users?.forEach((u) => {
        if (u.id) ids.add(u.id);
      });
    });
    return ids;
  }, [selectedUserList, selectedGroupList]);

  // Combined set of all already-added emails: selectedEmailList (raw typed
  // emails), plus selectedUserList and selectedGroupList's members' emails.
  // allSelectedUserIdSet above already folds in group members by id, so an
  // *identical* id is caught there -- but two different search results (a
  // contact vs. a TaskTag user) can represent the same person under
  // different ids with the same email. Without this, a different group's
  // nested row (or the top-level search dropdown) for that email stays
  // clickable, letting the user individually add it; dedupedSelectedGroupList's
  // claimedEmails would then silently reassign ownership away from the
  // group that already had it, instead of the UI visibly disabling the
  // duplicate up front.
  const allSelectedEmailSet = useMemo(() => {
    const emails = new Set(selectedEmailList.map((e) => e.title.toLowerCase()));
    selectedUserList.forEach((u) => {
      const email = (u as { email?: string | null }).email?.toLowerCase();
      if (email) emails.add(email);
    });
    selectedGroupList.forEach((g) => {
      g.users?.forEach((u) => {
        const email = (u as { email?: string | null }).email?.toLowerCase();
        if (email) emails.add(email);
      });
    });
    return emails;
  }, [selectedEmailList, selectedUserList, selectedGroupList]);

  // Combined set of all already-added group IDs
  const allSelectedGroupIdSet = useMemo(
    () => new Set(selectedGroupList.map((g) => g.id)),
    [selectedGroupList]
  );

  const contactIdSet = useMemo(
    () => new Set(contacts.map((c) => c.id)),
    [contacts]
  );

  const existingMemberIdSet = useMemo(
    () =>
      new Set(
        existingProjectMembers
          .filter((m) => m.id && !m.id.startsWith('invited-'))
          .map((m) => m.id)
      ),
    [existingProjectMembers]
  );

  const existingMemberEmailSet = useMemo(
    () =>
      new Set(
        existingProjectMembers
          .map((m) => (m.email ?? '').toLowerCase())
          .filter(Boolean)
      ),
    [existingProjectMembers]
  );

  // A pending invite's synthetic id ("invited-...") is deliberately excluded
  // from existingMemberIdSet above even though its real email is in
  // existingMemberEmailSet — addUserToSelected already guards against both.
  // Render checks that only looked at id missed this: a real user account
  // whose email already has a pending invite showed up enabled with no
  // badge, and clicking it silently no-op'd inside addUserToSelected's own
  // guard. Share one predicate so every "is this already a member/invite"
  // render check matches what actually blocks submission.
  const isExistingProjectMemberOrInvite = (candidate: {
    id?: string | null;
    email?: string | null;
  }): boolean => {
    const emailNorm = (candidate.email ?? '').toLowerCase();
    return (
      (!!candidate.id && existingMemberIdSet.has(candidate.id)) ||
      (!!emailNorm && existingMemberEmailSet.has(emailNorm))
    );
  };

  const filteredList = useMemo(() => {
    // Fall back to the full local contacts list until a gated search has
    // actually settled, so 1-2 character terms don't flash an empty
    // dropdown — TA-10166.
    return (hasSearched ? listContacts : (contacts ?? [])).map(
      (u) => ({ ...u, role: defaultRole }) as UserSearchType
    );
  }, [contacts, hasSearched, defaultRole, listContacts]);

  const _filteredTasktagUsers = useMemo(() => {
    if (!searchVal) return [];

    return listTasktagUsers.filter(
      (u) =>
        !selectedUserIdSet.has(u.id) &&
        !contactIdSet.has(u.id) &&
        u.id !== me?.id
    );
  }, [listTasktagUsers, searchVal, selectedUserIdSet, contactIdSet, me?.id]);

  // Suggest an email invite item if not already present anywhere
  const suggestionList: StringSearchType[] = useMemo(() => {
    if (!searchVal.trim()) return [];

    const semail = getSuggestionEmail(searchVal);
    if (!semail) return [];

    const semailLower = semail.toLowerCase();

    const inContacts = filteredList.some(
      (t: any) => (t.email || '').toLowerCase() === semailLower
    );
    if (inContacts) return [];

    const inTasktagUsers = listTasktagUsers.some(
      (t: any) => (t.email || '').toLowerCase() === semailLower
    );
    if (inTasktagUsers) return [];

    if (emailExistsInEntities(listGroup, semailLower)) return [];

    return [
      {
        title: semail,
        role: defaultRole,
        __typename: 'StringSearchType',
      },
    ];
  }, [searchVal, filteredList, listTasktagUsers, listGroup, defaultRole]);

  const getDisplayName = (item: UserSearchType) => {
    if ('name' in item && item.name) return item.name;
    if ('firstName' in item && 'lastName' in item) {
      return `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim();
    }
    if ('title' in item) return item.title;
    return '';
  };

  const renderUser = (item: UserSearchType, highlight?: string) => {
    const displayName = getDisplayName(item);
    const isEmail = item.__typename === 'StringSearchType';

    return (
      <Box flex={1} flexDirection='row' alignItems='center'>
        {isEmail ? (
          <EmailAvatar width={40} />
        ) : (
          <Avatar
            disableAvatarPress={true}
            size='medium'
            id={(item as any).id}
            avatar={(item as any).avatar}
            label={displayName}
            isMultiple={false}
          />
        )}
        <Box mx='xs'>
          <Text
            variant='webBodySecondary'
            color='textPrimary'
            width={500}
            flexShrink={1}
            ellipsizeMode='tail'
            numberOfLines={1}>
            <Highlighter
              autoEscape
              highlightStyle={{ backgroundColor: theme.colors.yellowBright }}
              searchWords={highlight ? [highlight] : []}
              textToHighlight={displayName}
            />
          </Text>
          {!isEmail && 'email' in item && Boolean(item.email) && (
            <Text
              mr='m'
              width={500}
              flexShrink={1}
              variant='webBodyHintSubline'
              color='grey04'
              marginTop='xxs'
              ellipsizeMode='tail'
              numberOfLines={1}>
              <Highlighter
                autoEscape
                highlightStyle={{ backgroundColor: theme.colors.yellowBright }}
                searchWords={highlight ? [highlight] : []}
                textToHighlight={item.email}
              />
            </Text>
          )}
        </Box>
      </Box>
    );
  };

  const addUserToSelected = useCallback(
    (user: any) => {
      const emailNorm = (user.email ?? '').toLowerCase();
      const alreadyById =
        allSelectedUserIdSet.has(user.id) || existingMemberIdSet.has(user.id);
      const alreadyByEmail =
        emailNorm &&
        (allSelectedEmailSet.has(emailNorm) ||
          existingMemberEmailSet.has(emailNorm));
      if (alreadyById || alreadyByEmail) {
        setText('');
        setFocusOnSearch(false);
        return;
      }
      const userWithRole: UserSearchType = { ...user, role: defaultRole };
      setSelectedUserList((prev) => [...prev, userWithRole]);
      setText('');
      setFocusOnSearch(false);
    },
    [
      defaultRole,
      allSelectedUserIdSet,
      existingMemberIdSet,
      allSelectedEmailSet,
      existingMemberEmailSet,
    ]
  );

  const addEmailToSelected = useCallback(
    (email: string) => {
      const emailNorm = email.toLowerCase();
      if (
        allSelectedEmailSet.has(emailNorm) ||
        existingMemberEmailSet.has(emailNorm)
      ) {
        setText('');
        setFocusOnSearch(false);
        return;
      }
      const emailItem: StringSearchType = {
        title: email,
        __typename: 'StringSearchType',
        role: defaultRole,
      };
      setSelectedEmailList((prev) => [...prev, emailItem]);
      setText('');
      setFocusOnSearch(false);
    },
    [defaultRole, allSelectedEmailSet, existingMemberEmailSet]
  );

  const addEntityToSelected = useCallback(
    (entity: Group) => {
      // TA-10800: store each group's *full*, original membership here —
      // cross-selection overlap is resolved reactively by
      // dedupedSelectedGroupList below, not by filtering at add time.
      // Filtering here (an earlier version of this fix) permanently
      // dropped a shared member from every group but the first, so
      // removing that first group later lost the person from the
      // selection entirely instead of handing them back to whichever
      // group is still selected. Still dedupe within the group's own
      // member list, defensively, in case the backend ever returns one
      // twice.
      const seenIds = new Set<string>();
      const entityUsers: UserSearchType[] = (entity.users ?? [])
        .filter((u) => {
          if (!u.id || seenIds.has(u.id)) return false;
          seenIds.add(u.id);
          return true;
        })
        .map((u) => ({
          ...u,
          role: defaultRole,
          __typename: 'User' as const,
        }));

      const asGroup: SelectedGroupType = {
        ...entity,
        users: entityUsers,
      } as SelectedGroupType;

      setSelectedGroupList((prev) => [...prev, asGroup]);
      setText('');
      setFocusOnSearch(false);
    },
    [defaultRole]
  );

  // TA-10800: the reactive, non-destructive dedup. For the *current*
  // selection state, decide which selected group "keeps" an overlapping
  // member and which loses them — to an individually-selected user, an
  // existing project member/invite (by id, or by email for a pending
  // invite whose synthetic id existingMemberIdSet deliberately excludes),
  // or an earlier-selected group. Recomputed from each group's untouched
  // original membership whenever the selection changes, so removing an
  // earlier group correctly hands an overlapping member back to a later
  // one instead of losing them.
  const dedupedSelectedGroupList = useMemo(() => {
    const claimedIds = new Set(selectedUserList.map((u) => u.id));
    // Seed from both the raw typed-email list AND individually-selected
    // users' emails -- a contact and a TaskTag user can represent the same
    // real person under different ids but the same email, and only the
    // id-based check above would catch that overlap.
    const claimedEmails = new Set([
      ...selectedEmailList.map((e) => e.title.toLowerCase()),
      ...selectedUserList
        .map((u) => (u as { email?: string | null }).email?.toLowerCase())
        .filter((email): email is string => !!email),
    ]);

    return selectedGroupList.map((group) => {
      const users = (group.users ?? []).filter((u) => {
        // Group members are always the User-shaped branch of
        // UserSearchType (StringSearchType is only for raw email
        // entries), but the union still requires a narrowing cast here.
        const emailNorm = (
          (u as { email?: string | null }).email ?? ''
        ).toLowerCase();
        const alreadyById =
          !!u.id && (claimedIds.has(u.id) || existingMemberIdSet.has(u.id));
        const alreadyByEmail =
          !!emailNorm &&
          (claimedEmails.has(emailNorm) ||
            existingMemberEmailSet.has(emailNorm));
        if (alreadyById || alreadyByEmail) return false;
        if (u.id) claimedIds.add(u.id);
        if (emailNorm) claimedEmails.add(emailNorm);
        return true;
      });
      return { ...group, users };
    });
  }, [
    selectedGroupList,
    selectedUserList,
    selectedEmailList,
    existingMemberIdSet,
    existingMemberEmailSet,
  ]);

  // dedupedSelectedGroupList physically
  // drops an overlapping member from every group but the one that "keeps"
  // them — correct for submission (usersFromGroups/groupUserIds below), but
  // the SELECTED-groups panel used the same list for rendering, so
  // expanding a group that lost a member rendered no row for them at all —
  // not even a disabled/already-added one, unlike every other place in this
  // file that surfaces a duplicate. This keeps every original member for
  // display, tagging the ones dedupedSelectedGroupList excluded so the
  // renderer can show them as a disabled, already-added row instead of
  // silently dropping them.
  const renderableSelectedGroupList = useMemo(() => {
    const keptIdsByGroup = new Map(
      dedupedSelectedGroupList.map((g) => [
        g.id,
        new Set((g.users ?? []).map((u) => u.id).filter(Boolean) as string[]),
      ])
    );
    return selectedGroupList.map((group) => {
      const keptIds = keptIdsByGroup.get(group.id) ?? new Set<string>();
      const users = (group.users ?? []).map((u) => ({
        ...u,
        isDuplicateInSelection: !!u.id && !keptIds.has(u.id),
      }));
      return { ...group, users };
    });
  }, [selectedGroupList, dedupedSelectedGroupList]);

  const renderAlreadyAddedBadge = (isAdded: boolean): ReactElement | null =>
    isAdded ? (
      <Text variant='webBodyHintSubline' color='grey04' marginLeft='xs'>
        {t('shared:alreadyAdded')}
      </Text>
    ) : null;

  const renderProjectMemberBadge = (isMember: boolean): ReactElement | null =>
    isMember ? (
      <Text variant='webBodyHintSubline' color='grey04' marginLeft='xs'>
        {t('shared:memberOfThisProject')}
      </Text>
    ) : null;

  const renderNestedUserRow = (
    entityId: string,
    user: User,
    keyPrefix: string
  ) => {
    const isExistingMember = isExistingProjectMemberOrInvite(user);
    // id alone misses a different search
    // result (contact vs. TaskTag user) representing the same person under
    // a different id but the same email -- that row stayed clickable,
    // letting the user individually add it and silently reassign ownership
    // away from the group that already had it.
    const emailNorm = (user.email ?? '').toLowerCase();
    const isUserAlreadyAdded =
      !isExistingMember &&
      (allSelectedUserIdSet.has(user.id) ||
        (!!emailNorm && allSelectedEmailSet.has(emailNorm)));
    const isDisabled = isExistingMember || isUserAlreadyAdded;
    return (
      <SimpleHoverable
        key={`${keyPrefix}-${entityId}:${user.id}`}
        accessibilityLabel={`Add Member | ${getDisplayName({
          ...user,
          role: defaultRole,
          __typename: 'User',
        })}`}
        onPress={() => {
          if (!isDisabled) addUserToSelected(user);
        }}>
        {(hoverUser) => (
          <Box
            flex={1}
            pr='m'
            py='s'
            borderBottomColor='grey02'
            borderBottomWidth={1}
            style={{
              ...getHoverRowStyle(hoverUser, isDisabled),
              paddingLeft: theme.spacing.xxl,
            }}
            justifyContent='center'>
            <Box flexDirection='row' alignItems='center' flex={1}>
              <Box flex={1}>
                {renderUser(
                  { ...user, role: defaultRole, __typename: 'User' },
                  searchVal
                )}
              </Box>
              {renderProjectMemberBadge(isExistingMember)}
              {renderAlreadyAddedBadge(isUserAlreadyAdded)}
            </Box>
          </Box>
        )}
      </SimpleHoverable>
    );
  };

  const renderEntityDropdownItem = (entity: Group) => {
    const isAlreadyAdded = allSelectedGroupIdSet.has(entity.id);
    const isExpanded = !searchExpandedGroupIds.includes(entity.id);
    const users = entity.users ?? [];
    const label = `Add Member Group | ${entity.name}`;

    return (
      <SimpleHoverable accessibilityLabel={label}>
        {(hover) => (
          <Box>
            <Box
              flex={1}
              px='m'
              py='s'
              borderBottomColor='grey02'
              borderBottomWidth={1}
              style={getHoverRowStyle(hover, isAlreadyAdded)}
              justifyContent='center'>
              <Box flex={1} flexDirection='row' alignItems='center'>
                <TouchableOpacity
                  accessibilityLabel={`${
                    isExpanded ? 'Collapse' : 'Expand'
                  } Group | ${entity.name}`}
                  onPress={() => toggleSearchGroupExpanded(entity.id)}>
                  <Icon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    variant='l'
                    marginHorizontal='xs'
                    color='grey05'
                  />
                </TouchableOpacity>

                <TouchableOpacity
                  accessibilityLabel={label}
                  style={{ flex: 1 }}
                  disabled={isAlreadyAdded}
                  onPress={() => {
                    if (!isAlreadyAdded) addEntityToSelected(entity);
                  }}>
                  <Box ml='xs'>
                    <Box flexDirection='row' alignItems='center'>
                      <Text
                        variant='webBodySecondary'
                        color={isAlreadyAdded ? 'grey04' : 'textPrimary'}
                        numberOfLines={1}
                        style={{ flex: 1 }}>
                        <Highlighter
                          autoEscape
                          highlightStyle={{
                            backgroundColor: theme.colors.yellowBright,
                          }}
                          searchWords={[searchVal]}
                          textToHighlight={entity.name}
                        />
                      </Text>
                      {renderAlreadyAddedBadge(isAlreadyAdded)}
                    </Box>
                    <Text
                      variant='webBodyHintSubline'
                      color='grey04'
                      marginTop='xxs'
                      numberOfLines={1}>
                      {`${users.length} members`}
                    </Text>
                  </Box>
                </TouchableOpacity>
              </Box>
            </Box>

            {isExpanded &&
              users.map((user) =>
                renderNestedUserRow(entity.id, user, 'group')
              )}
          </Box>
        )}
      </SimpleHoverable>
    );
  };

  const keyExtractor = useCallback(
    (item: any, index: number) =>
      item?.__typename === 'str'
        ? `separator-${index}`
        : (item?.id ?? item?.title ?? `item-${index}`),
    []
  );

  const renderDropdownItem = useCallback(
    ({ item }: ListRenderItemInfo<any>) => {
      // "More on TaskTag" separator - match task invite modal rendering
      if (item.__typename === 'str') {
        return (
          <SimpleHoverable accessibilityLabel='More on TaskTag'>
            {() => (
              <Box
                flex={1}
                px='m'
                py='s'
                flexDirection='row'
                alignItems='center'
                borderBottomColor='grey02'
                borderBottomWidth={1}
                style={{ backgroundColor: theme.colors.white }}
                justifyContent='center'>
                <Box flex={1}>
                  <Text
                    variant='webBodySecondary'
                    color='textPrimary'
                    flexShrink={1}
                    ellipsizeMode='tail'
                    numberOfLines={1}>
                    {item.name}
                  </Text>
                </Box>
              </Box>
            )}
          </SimpleHoverable>
        );
      }

      if (item.__typename === 'Group') {
        return renderEntityDropdownItem(item);
      }

      if (item.__typename === 'StringSearchType') {
        const isExistingProjectEmail = existingMemberEmailSet.has(
          item.title.toLowerCase()
        );
        const isEmailAlreadyAdded =
          !isExistingProjectEmail &&
          allSelectedEmailSet.has(item.title.toLowerCase());
        const isEmailDisabled = isExistingProjectEmail || isEmailAlreadyAdded;
        return (
          <SimpleHoverable
            accessibilityLabel={`Add Member | ${item.title}`}
            onPress={() => {
              if (!isEmailDisabled) addEmailToSelected(item.title);
            }}>
            {(hover) => (
              <Box
                flex={1}
                px='m'
                py='s'
                flexDirection='row'
                alignItems='center'
                borderBottomColor='grey02'
                borderBottomWidth={1}
                style={getHoverRowStyle(hover, isEmailDisabled)}
                justifyContent='center'>
                <Box flex={1} flexDirection='row' alignItems='center'>
                  <EmailAvatar width={40} />
                  <Box mx='xs'>
                    <Text
                      variant='webBodySecondary'
                      color={isEmailDisabled ? 'grey04' : 'textPrimary'}
                      width={500}
                      flexShrink={1}
                      ellipsizeMode='tail'
                      numberOfLines={1}>
                      <Text>{'Invite: '}</Text>
                      <Highlighter
                        autoEscape
                        highlightStyle={{
                          backgroundColor: theme.colors.yellowBright,
                        }}
                        searchWords={[searchVal]}
                        textToHighlight={item.title}
                      />
                    </Text>
                  </Box>
                </Box>
                {renderProjectMemberBadge(isExistingProjectEmail)}
                {renderAlreadyAddedBadge(isEmailAlreadyAdded)}
              </Box>
            )}
          </SimpleHoverable>
        );
      }

      const displayName = getDisplayName(item);
      const isExistingProjectUser = isExistingProjectMemberOrInvite(item);
      // Same email-under-a-different-id gap as renderNestedUserRow above
      // -- see that comment.
      const itemEmailNorm = (item.email ?? '').toLowerCase();
      const isUserAlreadyAdded =
        !isExistingProjectUser &&
        (allSelectedUserIdSet.has(item.id) ||
          (!!itemEmailNorm && allSelectedEmailSet.has(itemEmailNorm)));
      const isUserDisabled = isExistingProjectUser || isUserAlreadyAdded;
      return (
        <SimpleHoverable
          accessibilityLabel={`Add Member | ${displayName}`}
          onPress={() => {
            if (!isUserDisabled) addUserToSelected(item);
          }}>
          {(hover) => (
            <Box
              flex={1}
              px='m'
              py='s'
              borderBottomColor='grey02'
              borderBottomWidth={1}
              style={getHoverRowStyle(hover, isUserDisabled)}
              justifyContent='center'>
              <Box flexDirection='row' alignItems='center' flex={1}>
                <Box flex={1}>
                  {renderUser({ ...item, role: defaultRole }, searchVal)}
                </Box>
                {renderProjectMemberBadge(isExistingProjectUser)}
                {renderAlreadyAddedBadge(isUserAlreadyAdded)}
              </Box>
            </Box>
          )}
        </SimpleHoverable>
      );
    },
    [
      searchExpandedGroupIds,
      addEntityToSelected,
      toggleSearchGroupExpanded,
      addUserToSelected,
      addEmailToSelected,
      defaultRole,
      searchVal,
      getDisplayName,
      renderUser,
      allSelectedUserIdSet,
      allSelectedEmailSet,
      allSelectedGroupIdSet,
      existingMemberIdSet,
      existingMemberEmailSet,
    ]
  );

  const updateRoleForSelectedUser = useCallback(
    (userId: string, role: ProjectMemberRole) => {
      setSelectedUserList((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role } : u))
      );
    },
    []
  );

  const updateRoleForSelectedEmail = useCallback(
    (email: string, role: ProjectMemberRole) => {
      setSelectedEmailList((prev) =>
        prev.map((e) =>
          e.title.toLowerCase() === email.toLowerCase() ? { ...e, role } : e
        )
      );
    },
    []
  );

  const updateRoleForGroupUser = useCallback(
    (groupId: string, userId: string, role: ProjectMemberRole) => {
      setSelectedGroupList((prev) =>
        prev.map((g) => updateGroupUserRoleMapper(g, groupId, userId, role))
      );
    },
    []
  );

  const updateRoleForAllGroupUsers = useCallback(
    (groupId: string, role: ProjectMemberRole) => {
      setSelectedGroupList((prev) =>
        prev.map((g) => updateRoleForAllGroupUsersMapper(g, groupId, role))
      );
    },
    []
  );
  const updateRoleForAllGroupUsersMapper = (
    g: SelectedGroupType,
    groupId: string,
    role: ProjectMemberRole
  ): SelectedGroupType => {
    if (g.id !== groupId) return g;
    const nextUsers = (g.users ?? []).map((u) => ({ ...u, role }));
    return { ...g, users: nextUsers };
  };

  // Helper to confirm admin role change before applying
  const confirmAdminChange = useCallback(() => {
    if (!pendingAdminChange) return;
    const { type, id, groupId, role } = pendingAdminChange;
    switch (type) {
      case 'user':
        updateRoleForSelectedUser(id, role);
        break;
      case 'email':
        updateRoleForSelectedEmail(id, role);
        break;
      case 'groupUser':
        if (groupId) updateRoleForGroupUser(groupId, id, role);
        break;
      case 'allGroup':
        updateRoleForAllGroupUsers(id, role);
        break;
    }
    setShowAdminConfirmModal(false);
    setPendingAdminChange(null);
  }, [
    pendingAdminChange,
    updateRoleForSelectedUser,
    updateRoleForSelectedEmail,
    updateRoleForGroupUser,
    updateRoleForAllGroupUsers,
  ]);

  const removeSelectedUser = useCallback((userId: string) => {
    setSelectedUserList((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const removeSelectedEmail = useCallback((email: string) => {
    setSelectedEmailList((prev) =>
      prev.filter((e) => e.title.toLowerCase() !== email.toLowerCase())
    );
  }, []);

  const removeSelectedGroup = useCallback((groupId: string) => {
    setSelectedGroupList((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const handlePendingAdminChange = useCallback(
    (change: {
      type: 'user' | 'email' | 'groupUser';
      id: string;
      groupId?: string;
      name: string;
      role: ProjectMemberRole;
    }) => {
      setPendingAdminChange(change);
      setShowAdminConfirmModal(true);
    },
    []
  );

  const renderAssignItem = useCallback(
    ({ item }: ListRenderItemInfo<any>) => {
      if (item.__typename === 'Group') {
        const group = item as SelectedGroupType;
        const isExpanded = expandedGroupIds.includes(group.id);

        const renderGroupMemberRow = (user: UserSearchType) => {
          const onRoleChange = (role: ProjectMemberRole) =>
            updateRoleForGroupUser(group.id, user.id!, role);
          const onRemove = () => removeSelectedUser(user.id!);
          return (
            <SimpleHoverable
              key={`${group.id}:${user.id}`}
              accessibilityLabel={`Member | ${getDisplayName(user)}`}>
              {(hoverMember) => (
                <Box
                  flex={1}
                  px='xs'
                  py='s'
                  borderBottomColor='grey02'
                  borderBottomWidth={1}
                  flexDirection='row'
                  backgroundColor={hoverMember ? 'grey01' : 'white'}
                  alignItems='center'
                  style={{ paddingLeft: theme.spacing.xl }}>
                  <Box flex={1}>{renderUser(user)}</Box>
                  <RoleSelectorWithTrash
                    currentRole={user.role}
                    onRoleChange={onRoleChange}
                    onRemove={onRemove}
                    removeLabel={`Remove Member | ${getDisplayName(user)}`}
                    adminChangeConfig={{
                      type: 'groupUser',
                      id: user.id!,
                      groupId: group.id,
                      name: getDisplayName(user),
                    }}
                    onPendingAdminChange={handlePendingAdminChange}
                    canAssignAdminRole={canAssignAdminRole}
                  />
                </Box>
              )}
            </SimpleHoverable>
          );
        };

        // A member excluded here by dedupedSelectedGroupList (kept only in
        // renderableSelectedGroupList,
        // tagged) is still genuinely part of this group — just already
        // submitted via another selection. Show them as a disabled,
        // already-added row instead of letting them vanish from the group's
        // own member list.
        const renderDuplicateGroupMemberRow = (user: UserSearchType) => (
          <Box
            key={`${group.id}:${user.id}:dup`}
            accessibilityLabel={`Already Selected Member | ${getDisplayName(user)}`}
            flex={1}
            pl='xl'
            pr='xs'
            py='s'
            borderBottomColor='grey02'
            borderBottomWidth={1}
            flexDirection='row'
            alignItems='center'
            opacity={0.5}>
            <Box flex={1}>{renderUser(user)}</Box>
            {renderAlreadyAddedBadge(true)}
          </Box>
        );

        return (
          <Box key={`group-row:${group.id}`}>
            <SimpleHoverable
              accessibilityLabel={`${
                isExpanded ? 'Collapse' : 'Expand'
              } Group | ${group.name}`}
              onPress={() => toggleGroupExpanded(group.id)}>
              {(hover) => (
                <Box
                  flex={1}
                  px='xs'
                  py='s'
                  borderBottomColor='grey02'
                  borderBottomWidth={1}
                  flexDirection='row'
                  backgroundColor={hover ? 'grey01' : 'white'}
                  alignItems='center'>
                  <Box flex={1} flexDirection='row' alignItems='center'>
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      variant='l'
                      marginHorizontal='xs'
                      color='grey05'
                    />
                    <Box ml='xs'>
                      <Text
                        variant='webBodySecondary'
                        color='textPrimary'
                        numberOfLines={1}>
                        {group.name}
                      </Text>
                      <Text
                        variant='webBodyHintSubline'
                        color='grey04'
                        marginTop='xxs'
                        numberOfLines={1}>
                        {`${group.users?.length ?? 0} members`}
                      </Text>
                    </Box>
                  </Box>

                  <TouchableOpacity
                    accessibilityLabel={`Remove Group | ${group.name}`}
                    onPress={() => removeSelectedGroup(group.id)}>
                    <Box
                      ml='xs'
                      width={32}
                      height={32}
                      alignItems='center'
                      justifyContent='center'>
                      <Icon color='grey05' variant='s' name='Trash2' />
                    </Box>
                  </TouchableOpacity>
                </Box>
              )}
            </SimpleHoverable>

            {isExpanded &&
              (group.users as RenderableGroupMember[] | undefined)?.map(
                (user) =>
                  user.isDuplicateInSelection
                    ? renderDuplicateGroupMemberRow(user)
                    : renderGroupMemberRow(user)
              )}
          </Box>
        );
      }

      if (item.__typename === 'StringSearchType') {
        const emailItem = item as StringSearchType;
        return (
          <Box
            key={`email-row:${emailItem.title}`}
            flex={1}
            px='xs'
            py='s'
            borderBottomColor='grey02'
            borderBottomWidth={1}
            flexDirection='row'
            alignItems='center'>
            <Box flex={1}>{renderUser(emailItem)}</Box>
            <RoleSelectorWithTrash
              currentRole={emailItem.role}
              onRoleChange={(role) =>
                updateRoleForSelectedEmail(emailItem.title, role)
              }
              onRemove={() => removeSelectedEmail(emailItem.title)}
              removeLabel={`Remove Email | ${emailItem.title}`}
              adminChangeConfig={{
                type: 'email',
                id: emailItem.title,
                name: emailItem.title,
              }}
              onPendingAdminChange={handlePendingAdminChange}
              canAssignAdminRole={canAssignAdminRole}
            />
          </Box>
        );
      }

      const user = item as UserSearchType;
      return (
        <Box
          key={`user-row:${user.id}`}
          flex={1}
          px='xs'
          py='s'
          borderBottomColor='grey02'
          borderBottomWidth={1}
          flexDirection='row'
          alignItems='center'>
          <Box flex={1}>{renderUser(user)}</Box>
          <RoleSelectorWithTrash
            currentRole={user.role}
            onRoleChange={(role) =>
              updateRoleForSelectedUser(user.id as string, role)
            }
            onRemove={() => removeSelectedUser(user.id as string)}
            removeLabel={`Remove Member | ${getDisplayName(user)}`}
            adminChangeConfig={{
              type: 'user',
              id: user.id as string,
              name: getDisplayName(user),
            }}
            onPendingAdminChange={handlePendingAdminChange}
            canAssignAdminRole={canAssignAdminRole}
          />
        </Box>
      );
    },
    [
      expandedGroupIds,
      toggleGroupExpanded,
      removeSelectedGroup,
      removeSelectedUser,
      removeSelectedEmail,
      updateRoleForSelectedUser,
      updateRoleForSelectedEmail,
      updateRoleForGroupUser,
      updateRoleForAllGroupUsers,
      handlePendingAdminChange,
      canAssignAdminRole,
    ]
  );

  // TA-10800: built from the *deduped* group view, not raw selectedGroupList.
  // An individually-selected user whose id dedupedSelectedGroupList has
  // already excluded from every group (because claimedIds seeds from
  // selectedUserList first) must still render via visibleSelectedUsers
  // below — building this from the raw list hid them from both the
  // individual row and the (deduped, now-excluding-them) group row, even
  // though they were still correctly included in the submission (Copilot
  // review, PR #5912).
  const groupUserIds = useMemo(() => {
    const ids = new Set<string>();
    dedupedSelectedGroupList.forEach((g) => {
      g.users?.forEach((u) => {
        if (u.id) ids.add(u.id);
      });
    });
    return ids;
  }, [dedupedSelectedGroupList]);

  const visibleSelectedUsers = useMemo(
    () => selectedUserList.filter((u) => !groupUserIds.has(u.id as string)),
    [selectedUserList, groupUserIds]
  );

  // Build dropdown data once (avoid recreating array in render)
  const dropdownData = useMemo(() => {
    return [
      ...suggestionList,
      ...filteredList,
      ...listGroup,
      ...(_filteredTasktagUsers.length > 0
        ? [{ name: 'More on TaskTag', __typename: 'str' }]
        : []),
      ..._filteredTasktagUsers,
    ];
  }, [suggestionList, filteredList, listGroup, _filteredTasktagUsers]);

  const hasSelections =
    selectedUserList.length > 0 ||
    selectedEmailList.length > 0 ||
    selectedGroupList.length > 0;

  const hasEmailsNotInProject = useMemo(
    () =>
      selectedEmailList.some(
        (e) => !existingMemberEmailSet.has(e.title.toLowerCase())
      ),
    [selectedEmailList, existingMemberEmailSet]
  );

  // Detect whether the member list has actually changed from its initial state
  const hasChanges = useMemo(() => {
    // Pre-populated selections from banner flow count as pending invites
    if (
      treatInitialSelectionsAsChanges &&
      (initialSelectedUsers.length > 0 || initialSelectedEmails.length > 0)
    )
      return true;

    // Any groups added means a change
    if (selectedGroupList.length > 0) return true;

    // Compare user lists by id + role
    if (selectedUserList.length !== initialSelectedUsers.length) return true;
    const initialUserMap = new Map(
      initialSelectedUsers.map((u) => [u.id, u.role])
    );
    for (const u of selectedUserList) {
      if (!initialUserMap.has(u.id) || initialUserMap.get(u.id) !== u.role)
        return true;
    }

    // Compare email lists by title + role (normalized to lowercase)
    if (selectedEmailList.length !== initialSelectedEmails.length) return true;
    const initialEmailMap = new Map(
      initialSelectedEmails.map((e) => [e.title.toLowerCase(), e.role])
    );
    for (const e of selectedEmailList) {
      const key = e.title.toLowerCase();
      if (!initialEmailMap.has(key) || initialEmailMap.get(key) !== e.role)
        return true;
    }

    return false;
  }, [
    selectedUserList,
    selectedEmailList,
    selectedGroupList,
    initialSelectedUsers,
    initialSelectedEmails,
    treatInitialSelectionsAsChanges,
  ]);

  const onPressSendInvite = useCallback(async () => {
    // Flatten the deduped view (TA-10800), not the raw selectedGroupList —
    // an overlapping member must be submitted from exactly one group.
    const usersFromGroups: UserSearchType[] = dedupedSelectedGroupList.flatMap(
      (g) => g.users ?? []
    );

    const projectMembers: ProjectMemberRoleAttributes[] = [];

    // Add email invites
    selectedEmailList.forEach((e) => {
      projectMembers.push({
        email: e.title,
        role: e.role,
      });
    });

    // Add users with email addresses (for inviting users who aren't yet members)
    [...selectedUserList, ...usersFromGroups].forEach((u: any) => {
      if (u?.email) {
        projectMembers.push({
          email: u.email,
          role: u.role || ProjectMemberRole.Viewer,
        });
      }
    });

    // dedupedSelectedGroupList only dedupes a group's members against
    // selectedUserList/other groups —
    // selectedUserList itself can carry two different ids for the same
    // email (contact search and TaskTag-user search filter by id, not
    // email), and nothing downstream deduped that before this point. The
    // edit-project flow (ProjectMembers.web.tsx) passes this callback's
    // `invitations` straight to createProjectInvitation with no further
    // dedup, so an undeduped email here reproduces this ticket's exact
    // backend crash via that flow.
    const dedupedProjectMembers = dedupeProjectMemberInvites(projectMembers);

    if (dedupedProjectMembers.length > 0) {
      // Pass email invitations to the form so they are sent when the project is saved.
      // For new projects this goes through createProject; for existing projects through updateProject.
      const allSelectedUsers = [...selectedUserList, ...usersFromGroups];
      const hasExternalUsers =
        selectedEmailList.length > 0 ||
        allSelectedUsers.some(
          (u) => u.id === undefined || !contactIdSet.has(u.id)
        );
      await onAddEmailInvitations?.(
        dedupedProjectMembers,
        allSelectedUsers,
        hasExternalUsers
      );
      // TA-10892 #33: remember who the Owner invited during onboarding so the
      // chat list can open / highlight their chats.
      if (onboardingActive) {
        addOnboardingInvitees([
          ...selectedEmailList.map((e) => ({ email: e.title })),
          ...allSelectedUsers.map((u: { id?: string; email?: string }) => ({
            id: u.id,
            email: u.email,
          })),
        ]);
      }
    }

    globalThis.localStorage?.removeItem('non_member_assignee_emails');
    globalThis.localStorage?.removeItem(
      'non_member_assignee_emails_project_id'
    );
    if (projectId) {
      globalThis.localStorage?.removeItem(
        `non_member_assignee_emails_${projectId}`
      );
    }

    onClose?.();
  }, [
    contactIdSet,
    dedupedSelectedGroupList,
    onAddEmailInvitations,
    onboardingActive,
    onClose,
    projectId,
    selectedEmailList,
    selectedUserList,
  ]);

  const debouncedSendInvite = useMemo(
    () => debounce(onPressSendInvite, DEBOUNCE_MS),
    [onPressSendInvite]
  );

  useEffect(() => {
    return () => {
      debouncedSendInvite.cancel();
    };
  }, [debouncedSendInvite]);

  return (
    <Box
      py='xs'
      paddingHorizontal='m'
      justifyContent='flex-start'
      width={800}
      position='relative'>
      <Box flexDirection='row' mt='m' alignItems='center'>
        <Text flex={1} variant='labelLarge' color='textPrimary'>
          {t('shared:inviteOrAddMember')}
        </Text>

        {projectId && (
          <Box position='relative' marginRight='s'>
            <TooltipWebV3
              marginOffset={8}
              open={copyLinkTooltipMode !== null}
              disableHoverListener
              disableFocusListener
              disableTouchListener
              placement='bottom'
              variant={copyLinkTooltipMode === 'hover' ? 'dark' : 'light'}
              title={
                copyLinkTooltipMode === 'copied' ? (
                  <Box flexDirection='row'>
                    <Icon
                      name='CheckCircle'
                      color='greenSecondary'
                      variant='s'
                    />
                    <Text ml='xs' variant='labelSmall' color='black'>
                      Link copied!
                    </Text>
                  </Box>
                ) : (
                  <Text ml='xxs' variant='labelSmall' color='white'>
                    Copy link to invite
                  </Text>
                )
              }>
              <button
                aria-label='Copy Link to Invite'
                onMouseEnter={() => {
                  setIsCopyLinkHovered(true);
                  if (!suppressCopyHoverTooltip) {
                    setCopyLinkTooltipMode('hover');
                  }
                }}
                onMouseLeave={() => {
                  setIsCopyLinkHovered(false);
                  if (copyLinkTooltipMode === 'hover') {
                    setCopyLinkTooltipMode(null);
                  }
                  setSuppressCopyHoverTooltip(false);
                }}
                onClick={handleCopyLink}
                style={{
                  display: 'inline-flex',
                  cursor: 'pointer',
                  alignItems: 'center',
                  padding: '2px 4px',
                  borderRadius: 4,
                  background: isCopyLinkHovered ? theme.colors.grey01 : 'none',
                  border: 'none',
                }}>
                <Icon color='blue' variant='s' name='Link' marginRight='xs' />
                <Text variant='labelSmall' color='blue'>
                  Copy Link
                </Text>
              </button>
            </TooltipWebV3>
          </Box>
        )}

        <WebIcon
          accessibilityLabel='Close'
          name='X'
          variant='l'
          color='grey05'
          onPress={onClose}
        />
      </Box>

      <Box mt='s'>
        <AddMembersInput
          focusOnSearch={focusOnSearch}
          setFocusOnSearch={setFocusOnSearch}
          searchVal={searchVal}
          setText={setText}
          inputRef={inputRef}
          placeholder='Add members by email, name or group'
        />
      </Box>

      {shouldShowDropdown && (
        <Box
          position='absolute'
          top={64 + 16 + 48 + 4}
          left={16}
          right={16}
          borderRadius='xs'
          borderWidth={1}
          borderColor='grey02'
          backgroundColor='white'
          style={{
            maxHeight: 440,
            minHeight: 180,
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 10000,
            boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
          }}>
          {contactsLoading ? (
            <Box paddingHorizontal='m' paddingVertical='xxs'>
              <SkeletonList
                row={(index) => <AssigneeRowSkeleton key={index} />}
              />
            </Box>
          ) : (
            <FlatList
              keyExtractor={keyExtractor}
              keyboardShouldPersistTaps='handled'
              data={dropdownData}
              renderItem={renderDropdownItem}
              ListEmptyComponent={EmptyListBox}
              contentContainerStyle={{ paddingVertical: 4 }}
            />
          )}
        </Box>
      )}

      {!hasSelections && (
        <Box mt='s' alignItems='center'>
          <Image
            style={{ width: 86, height: 86 }}
            source={require('../../../assets/images/teammates.png')}
          />
          <Text
            mt='xs'
            mb='xxxs'
            flex={1}
            textAlign='center'
            variant='webBodySecondary'
            color='textPrimary'>
            {t('shared:bringYourTeamOnBoard')}
          </Text>
          <Text
            my='xs'
            flex={1}
            textAlign='center'
            variant='webBodyHintSubline'
            color='grey04'>
            {t('shared:inviteTeammatesOrContactGroups')}
          </Text>
        </Box>
      )}

      {hasSelections && (
        <Box minHeight={56} maxHeight={56 * 6}>
          <FlatList
            keyExtractor={keyExtractor}
            keyboardShouldPersistTaps='handled'
            data={[
              ...selectedEmailList,
              ...renderableSelectedGroupList,
              ...visibleSelectedUsers,
            ]}
            extraData={[
              selectedUserList,
              selectedEmailList,
              renderableSelectedGroupList,
            ]}
            renderItem={renderAssignItem}
            ListEmptyComponent={EmptyListBox}
            contentContainerStyle={{ paddingVertical: 4 }}
          />
        </Box>
      )}

      <Button
        disabled={
          isLoading || !hasSelections || (!hasChanges && !hasEmailsNotInProject)
        }
        onPress={async () => {
          setIsLoading(true);
          try {
            await onPressSendInvite();
          } finally {
            setIsLoading(false);
          }
        }}
        height={48}
        fullWidth
        backgroundColor={
          isLoading || !hasSelections || (!hasChanges && !hasEmailsNotInProject)
            ? 'grey02'
            : 'black'
        }
        marginTop='m'
        marginBottom='xxxs'
        accessibilityLabel='Send Invite'>
        {isLoading ? (
          <Box height={32}>
            <ActivityIndicatorLoading color='white' />
          </Box>
        ) : (
          <Text
            variant='labelSmall'
            color={
              hasSelections && (hasChanges || hasEmailsNotInProject)
                ? 'white'
                : 'grey04'
            }>
            {t('shared:sendInvite')}
          </Text>
        )}
      </Button>

      <Box
        mt='xs'
        height={16}
        mb='m'
        flexDirection='row'
        justifyContent='center'
        alignItems='center'>
        <Icon color='grey04' variant='s' name='Pending' />
        <Text
          ml='xxs'
          textAlign='center'
          variant='webBodyHintSubline'
          color='grey04'>
          {t('shared:invitationsExpireAfter7Days')}
        </Text>
      </Box>
      {showLinkCopiedToast &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role='alert'
            aria-live='polite'
            style={{
              position: 'fixed',
              left: '50%',
              bottom: theme.spacing.xl,
              transform: 'translateX(-50%)',
              zIndex: 999999,
            }}>
            <TaskCreatedToast
              title='Link copied'
              message='Paste it to any messaging app to invite your team.'
              duration={3000}
              minWidth={380}
            />
          </div>,
          document.body
        )}

      {showAdminConfirmModal && (
        <ConfirmModal
          showModal={showAdminConfirmModal}
          isAlert={false}
          padding='l'
          onClose={() => {
            setShowAdminConfirmModal(false);
            setPendingAdminChange(null);
          }}
          onPress={confirmAdminChange}
          buttonText={tModels('projects.members.confirmAdmin')}
          title={tModels('projects.members.changeToAdmin')}
          message={
            <Text>
              {tModels('projects.members.changeToAdminMessage', {
                name: pendingAdminChange?.name ?? '',
              })}
            </Text>
          }
        />
      )}
    </Box>
  );
};
